import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule, SortDirection } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { IPayrollSettingsList } from '../../../../pages/dashboard/models/payroll-settings-list.interface';
import { PayrollSettingsService } from '../../../../pages/dashboard/services/payroll-settings.service';
import { ThemesService } from '../../../../services/themes.service';
import { DealStatusEnum } from '../../../advance-application/models/deal-status.enum';
import { DealTransactionStatusEnum } from '../../../advance-application/models/deal-transaction-status.enum';
import { IExternalDealDetailsDTO } from '../../../advance-application/models/external-deal.interface';
import { TransactionStatusEnum } from '../../../advance-application/models/transaction-status.enum';
import { DealService } from '../../../advance-application/services/deal.service';
import { ProductEnum } from '../../../authentication/models/product.enum';
import { User } from '../../../authentication/models/v1-api.interface';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import { BankInfoPipe } from '../../../pipes/bank-info.pipe';
import { ConvertToDealTransactionStatusPipe } from '../../../pipes/convertToDealTransactionStatus.pipe';
import { CurrencyJsPipe } from '../../../pipes/currency-js.pipe';
import { DatePipe } from '../../../pipes/date.pipe';
import { LocalisePipe } from '../../../pipes/localise.pipe';
import { DealStatusWithColorComponent } from '../../../shared/components/deal-status-with-color/deal-status-with-color.component';
import { PreviewDocumentComponent } from '../../../shared/components/preview-document/preview-document.component';
import { IAgentCommissionTransaction } from '../../../shared/models/agent-commission-transaction.interface';
import { DownloadEnum } from '../../../shared/models/download.enum';
import { FormatDateEnum } from '../../../shared/models/format-date.enum';
import { TransactionTypeEnum } from '../../../shared/models/transaction-type.enum';
import { FileNamePipe } from '../../../shared/pipes/file-name.pipe';
import { FullAddressPipe } from '../../../shared/pipes/full-address.pipe';
import { AccountService } from '../../../shared/services/account.service';
import { BrokerService } from '../../../shared/services/broker.service';
import { DocumentService } from '../../../shared/services/document.service';
import { SharedService } from '../../../shared/services/shared.service';
import { ConfirmDepositDistributionsComponent } from '../confirm-deposit-distributions/confirm-deposit-distributions.component';

@Component({
  selector: 'app-commission-details',
  templateUrl: './commission-details.component.html',
  styleUrls: ['./commission-details.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatDialogModule,
    LocalisePipe,
    CurrencyJsPipe,
    DatePipe,
    FullAddressPipe,
    BankInfoPipe,
    PreviewDocumentComponent,
    FileNamePipe,
    DealStatusWithColorComponent,
    ConvertToDealTransactionStatusPipe,
  ],
})
export class CommissionDetailsComponent implements OnInit, OnDestroy {
  @Input() queryParams: any;

  protected readonly TransactionTypeEnum = TransactionTypeEnum;
  public dealId: number;
  public loading = true;
  private subscriptions: Subscription[] = [];
  public paymentDueStatuses = [
    DealStatusEnum.GracePeriod,
    DealStatusEnum.Deferred,
    DealStatusEnum.Late,
    DealStatusEnum.Defaulted,
  ];
  public formatDateEnum = FormatDateEnum;
  public dealStatusEnum = DealStatusEnum;
  public readonly dealTransactionStatusEnum = DealTransactionStatusEnum;
  public readonly TransactionStatusEnum = TransactionStatusEnum;
  public notConfirmedDealsTotal = 0;
  public externalDeal: IExternalDealDetailsDTO;
  public limit = 30;
  public page = 1;
  public sortBy = 'depositAmount';
  public sortDirection?: SortDirection;
  public user: User = this.authenticationService.getProfileState();
  public readonly productEnum = ProductEnum;
  public loadingFile = false;
  public error = '';

  public agentCommissionTransactions: IAgentCommissionTransaction[] = [];
  public payrollSettingsList: IPayrollSettingsList[] = [];

  public columnsPayroll = [
    'accountNickname',
    'paymentDate',
    'account',
    'distributionAmount',
    'depositAmount',
    'depositStatus',
  ];
  public downloadEnum = DownloadEnum;

  constructor(
    private activatedRoute: ActivatedRoute,
    private dealService: DealService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private accountService: AccountService,
    private brokerService: BrokerService,
    private documentService: DocumentService,
    private toastrService: ToastrService,
    private payrollSettingsService: PayrollSettingsService,
    private sharedService: SharedService,
    private themesService: ThemesService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    if (this.queryParams) {
      this.byQueryParams(this.queryParams);
    } else {
      this.subscriptions.push(
        this.activatedRoute.params.subscribe((params) =>
          this.byQueryParams(params as any),
        ),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public byQueryParams(queryParams: { id: string }): void {
    this.dealId = parseInt(queryParams.id, 10);
    this.load(this.dealId);
  }

  private async load(dealId: number): Promise<void> {
    this.loading = true;

    try {
      this.externalDeal = await firstValueFrom(
        this.dealService.getExternalDeal(dealId),
      );

      this.agentCommissionTransactions = [
        ...this.externalDeal?.agentCommissionTransactions,
      ].sort((trx1, trx2) => {
        return this.sortDirection === 'asc'
          ? trx1?.amount.value - trx2?.amount?.value
          : trx2?.amount?.value - trx1?.amount?.value;
      });
      this.payrollSettingsList = await firstValueFrom(
        this.payrollSettingsService.get(),
      );
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    }

    this.loading = false;
  }

  public setSort(sortBy?: string, sortDirection?: SortDirection): void {
    this.sortBy = sortBy || 'depositAmount';
    this.sortDirection = sortDirection;
    this.load(this.dealId);
  }

  public confirmDepositDistributions(externalDealId: number): void {
    const ref = this.dialog.open(ConfirmDepositDistributionsComponent, {
      ...ConfirmDepositDistributionsComponent.DIALOG_CONFIG,
      panelClass: [
        'dialog-without-padding',
        this.themesService.getDarkThemeClass(),
      ],
      backdropClass: this.themesService.getDarkThemeClass(),
    });
    ref.componentInstance.externalDealId = externalDealId;
    ref
      .afterClosed()
      .pipe(filter((success) => !!success))
      .subscribe(() => {
        this.load(this.dealId);
      });
  }
}
